import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Sparkles, Heart, Lightbulb, Megaphone, HandshakeIcon, Users, Rocket } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { STUDENT_AREAS } from "@/constants/brand";

const WAYS = [
  { Icon: Lightbulb,  title: "Share Ideas",      body: "Flavour ideas, packaging, marketing — bring what you have, big or small." },
  { Icon: Megaphone,  title: "Help Promote",     body: "Tell your friends. Post on your campus. Make Pop Panda known where you are." },
  { Icon: Heart,      title: "Collaborate",      body: "Photography, content, design, video, food production — work with us on real projects." },
  { Icon: Rocket,     title: "Partner With Us",  body: "Run a stall, host a tasting, pitch a partnership for your college or community." },
];

export default function StudentOps() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", college: "", course: "", year_of_study: "",
    interest_areas: [], portfolio_url: "", why_join: "",
  });

  const toggleArea = (a) => setForm({
    ...form,
    interest_areas: form.interest_areas.includes(a) ? form.interest_areas.filter((x) => x !== a) : [...form.interest_areas, a],
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/student-applications", form);
      setSubmitted(true);
      toast.success("Thank you. We'll be in touch soon!");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (submitted) {
    return (
      <div data-testid="student-done" className="section-pad">
        <div className="container-pp text-center max-w-xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Received</div>
          <h1 className="editorial-h1 text-5xl mt-3">Welcome aboard.</h1>
          <p className="mt-4 text-brand-charcoal/70">Thanks, {form.full_name.split(" ")[0]}. Someone from the Pop Panda team will reach out personally — usually within a week.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="student-page" className="section-pad">
      <div className="container-pp">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center gap-2"><Sparkles size={12} /> Students × Pop Panda</div>
            <h1 className="editorial-h1 text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]">
              We're building Pop Panda — <em className="font-display italic">come build with us.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/75 leading-relaxed">
            <p className="text-lg">We welcome any student who'd like to contribute, collaborate, share ideas, help promote the brand, participate in projects, or partner with us in meaningful ways.</p>
            <p className="mt-4 font-medium text-brand-charcoal">Skills and enthusiasm matter more than qualifications. Anyone can apply.</p>
          </div>
        </div>

        {/* Ways to get involved */}
        <div className="mt-20" data-testid="student-ways">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">A few ways to get involved</div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WAYS.map((w) => (
              <div key={w.title} className="card-elevated p-7 hover:-translate-y-1 transition-transform duration-300">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-yellow/25">
                  <w.Icon size={20} className="text-brand-charcoal" />
                </div>
                <div className="font-display text-2xl mt-5">{w.title}</div>
                <p className="mt-2 text-sm text-brand-charcoal/70 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="mt-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Tell us about you</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-none">Just say hello.</h2>
            <p className="mt-5 text-brand-charcoal/70 leading-relaxed">No formal application. No CV. Tell us who you are, what you'd love to help with, and how to reach you. We read every message.</p>
            <div className="mt-8 card-elevated p-6 bg-brand-yellow/15 border border-brand-yellow/40">
              <div className="font-display text-xl">No prerequisites.</div>
              <p className="mt-2 text-sm text-brand-charcoal/75">First-year or final-year. Engineering or design or arts. Confident or shy. Everyone gets a reply.</p>
            </div>
          </div>

          <form className="lg:col-span-7 card-elevated p-8 lg:p-10 space-y-5" onSubmit={submit} data-testid="student-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Your Name</label><input data-testid="st-name" className="pp-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email</label><input type="email" data-testid="st-email" className="pp-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</label><input data-testid="st-phone" className="pp-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">College / Institution (optional)</label><input data-testid="st-college" className="pp-input" value={form.college} onChange={(e) => setForm({ ...form, college: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Course (optional)</label><input data-testid="st-course" className="pp-input" value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Year of study (optional)</label><input data-testid="st-year" className="pp-input" value={form.year_of_study} onChange={(e) => setForm({ ...form, year_of_study: e.target.value })} /></div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">What would you like to help with? (pick any)</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {STUDENT_AREAS.map((a) => {
                  const active = form.interest_areas.includes(a);
                  return (
                    <button type="button" key={a} data-testid={`st-area-${a}`} onClick={() => toggleArea(a)}
                      className={`btn-pill text-xs ${active ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{a}</button>
                  );
                })}
              </div>
            </div>

            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Portfolio / Social link (optional)</label><input data-testid="st-port" className="pp-input" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} /></div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Tell us a little about yourself or what you'd love to do</label>
              <textarea data-testid="st-why" rows="4" className="pp-input" required value={form.why_join} onChange={(e) => setForm({ ...form, why_join: e.target.value })} placeholder="Anything works — an idea you have, a way you'd help, something you can offer." />
            </div>
            <button data-testid="st-submit" className="btn-pill btn-dark inline-flex items-center gap-2">Say hello <ArrowUpRight size={14} /></button>
          </form>
        </div>
      </div>
    </div>
  );
}
